/**
 * The ticker: work saved to the account, whenever there is wifi.
 *
 * > *"a like ticker which after every edit saves the changes WHEN WIFI IS ON, just like in google
 * > docs etc, and then if something does not get saved then maybe a save button"*
 *
 * Everything the app does still happens against IndexedDB first and finishes there. This is a
 * copier that runs afterwards: it takes records the device has written since the last time it
 * looked and puts them in `public.work`, and it takes records the account has gained elsewhere
 * and merges them in. Nothing in the drawing, reading or saving path waits on it, and switching
 * the network off changes only how often it succeeds.
 *
 * ── Four rules ─────────────────────────────────────────────────────────────────────────────
 *
 * **Newer wins, by the record's own stamp.** The same rule `lib/transfer.ts` applies to a file,
 * for the same reason: the machine running the merge is not evidence of anything, and the clock
 * that matters is the one that was ticking when the pen came off the paper.
 *
 * **A pull never deletes.** A record here and not there is a record this device has and the
 * server has not heard about yet — the next push tells it. There is no tombstone table, so a
 * delete does not travel, and that is deliberate for now: losing work to a sync is the one
 * failure this may not have, and "a deleted sheet came back" is a smaller fault than its
 * opposite.
 *
 * **An anonymous session syncs nothing.** It is a device, not an account; there would be no
 * second machine that could ever sign into it.
 *
 * **Failure is a sentence, never a spinner.** Offline is a state with a name and a Save now
 * button, not an error.
 */
import { computed, ref } from 'vue';

import { account } from './auth';
import { STORES, openDb } from './storage';
import { getSupabase, supabaseConfigured } from './supabase';
import { idOf, stampOf } from './transfer';

/** How long after the last edit the copier runs. Long enough that a stroke is not a round trip. */
export const QUIET_MS = 2500;

/** Rows per request. A page of sheets with images on them is the size that matters here. */
const PAGE = 200;

export type SyncState =
  /** No server in this build, or nobody signed in with a real account. */
  | 'off'
  /** Everything this device has written is on the account. */
  | 'saved'
  /** Edits are waiting for the quiet moment, or for the network. */
  | 'pending'
  | 'saving'
  /** There is no network. The work is safe on this device and will go up when there is. */
  | 'offline'
  /** The server refused. Says so, and keeps the Save now button live. */
  | 'trouble';

const state = ref<SyncState>('off');
const lastSavedAt = ref<number | null>(null);
const lastTrouble = ref<string | null>(null);

export const syncState = computed(() => state.value);
export const syncedAt = computed(() => lastSavedAt.value);
export const syncTrouble = computed(() => lastTrouble.value);

/** Whether this device is even a candidate: a real account, and credentials to reach one. */
export const syncable = computed(() =>
  supabaseConfigured() && account.value !== null && !account.value.anonymous);

// ── Watermarks ─────────────────────────────────────────────────────────────────────────────
//
// Two, per account, and they measure different clocks. `pushed` is the highest RECORD stamp this
// device has sent; `pulled` is the SERVER moment it last read up to. Neither is authoritative
// about the other, and collapsing them into one is how a device stops sending or stops receiving.

const pushKey = (id: string): string => `euclid.sync.pushed.${id}`;
const pullKey = (id: string): string => `euclid.sync.pulled.${id}`;

const readMark = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};

const writeMark = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch { /* private window */ }
};

const ALL = Object.values(STORES);

/** Every row of every store, straight out of IndexedDB. */
async function readAll(): Promise<Record<string, unknown[]>> {
  const db = await openDb();
  const out: Record<string, unknown[]> = {};
  for (const name of ALL) {
    if (!db.objectStoreNames.contains(name)) continue;
    out[name] = await new Promise<unknown[]>((resolve, reject) => {
      const request = db.transaction(name, 'readonly').objectStore(name).getAll();
      request.onsuccess = () => resolve(request.result as unknown[]);
      request.onerror = () => reject(request.error);
    });
  }
  return out;
}

/**
 * Merge rows the account has elsewhere into this device.
 *
 * One transaction per store, `get` before `put`, newer wins — and a row with no stamp is only
 * ever added, never used to overwrite one that is here. With nothing to compare, keeping what the
 * device holds is the answer that cannot lose anything.
 */
async function mergeIn(rows: { store: string; payload: unknown }[]): Promise<number> {
  if (rows.length === 0) return 0;
  const db = await openDb();
  const byStore = new Map<string, unknown[]>();
  for (const row of rows) {
    if (!db.objectStoreNames.contains(row.store)) continue;
    const held = byStore.get(row.store) ?? [];
    held.push(row.payload);
    byStore.set(row.store, held);
  }

  let taken = 0;
  for (const [name, payloads] of byStore) {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(name, 'readwrite');
      const store = tx.objectStore(name);
      for (const payload of payloads) {
        const id = idOf(payload);
        if (!id) continue;
        const existing = store.get(id);
        existing.onsuccess = () => {
          const here = existing.result as unknown;
          if (here === undefined) { store.put(payload); taken += 1; return; }
          if (stampOf(payload) > stampOf(here)) { store.put(payload); taken += 1; }
        };
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }
  return taken;
}

// ── The two halves ─────────────────────────────────────────────────────────────────────────

export interface SyncReport {
  pushed: number;
  pulled: number;
}

/**
 * One pass: send what is new here, take what is new there.
 *
 * Pull first. A device that pushes first would send a record it is about to be told is stale, and
 * the extra write is not free — but the real reason is the order of the watermarks: pulling
 * before pushing means a record written DURING the pass is caught by the next one rather than
 * skipped by a watermark that moved past it.
 */
export async function sync(force = false): Promise<SyncReport | null> {
  const who = account.value;
  if (!who || who.anonymous || !supabaseConfigured()) { state.value = 'off'; return null; }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    state.value = 'offline';
    return null;
  }

  const supabase = await getSupabase();
  if (!supabase) { state.value = 'off'; return null; }

  state.value = 'saving';
  lastTrouble.value = null;
  const report: SyncReport = { pushed: 0, pulled: 0 };

  try {
    // ── Pull ────────────────────────────────────────────────────────────────────────────
    const since = readMark(pullKey(who.id)) ?? '1970-01-01T00:00:00Z';
    let cursor = since;
    for (;;) {
      const { data, error } = await supabase
        .from('work')
        .select('store, record_id, payload, synced_at')
        .gt('synced_at', cursor)
        .order('synced_at', { ascending: true })
        .limit(PAGE);
      if (error) throw new Error(error.message);
      const page = data ?? [];
      if (page.length === 0) break;

      report.pulled += await mergeIn(page.map(row => ({
        store: String(row.store),
        payload: row.payload as unknown,
      })));
      cursor = String(page[page.length - 1]?.synced_at ?? cursor);
      writeMark(pullKey(who.id), cursor);
      if (page.length < PAGE) break;
    }

    // ── Push ────────────────────────────────────────────────────────────────────────────
    const highWater = Number(readMark(pushKey(who.id)) ?? '0');
    const everything = await readAll();
    const outgoing: {
      user_id: string; store: string; record_id: string; updated_at: number; payload: unknown;
    }[] = [];
    let highest = highWater;

    for (const [store, rows] of Object.entries(everything)) {
      for (const row of rows) {
        const id = idOf(row);
        if (!id) continue;
        const stamp = stampOf(row);
        // A stamped record goes up when it is newer than the last thing sent. An UNSTAMPED one —
        // a course, a chapter — has no way to say it changed, so it rides along on the first pass
        // of a device (`highWater === 0`) and whenever the button is pressed.
        if (stamp > highWater || (stamp === 0 && (force || highWater === 0))) {
          outgoing.push({
            user_id: who.id, store, record_id: id, updated_at: stamp, payload: row,
          });
          if (stamp > highest) highest = stamp;
        }
      }
    }

    for (let at = 0; at < outgoing.length; at += PAGE) {
      const { error } = await supabase
        .from('work')
        .upsert(outgoing.slice(at, at + PAGE), { onConflict: 'user_id,store,record_id' });
      if (error) throw new Error(error.message);
    }
    report.pushed = outgoing.length;
    // Only after the writes land. Moving it first would skip every record in a failed batch.
    writeMark(pushKey(who.id), String(highest));

    state.value = 'saved';
    lastSavedAt.value = Date.now();
    return report;
  } catch (error) {
    // A refusal names itself. `navigator.onLine` lies often enough — it is true on a wifi network
    // with no route out — that a failed request is the more reliable evidence of being offline.
    const said = error instanceof Error ? error.message : String(error);
    const looksOffline = /fetch|network|Failed to fetch|load failed/i.test(said);

    /*
     * A build whose database has not had `supabase/0005_workspace_sync.sql` run against it yet.
     *
     * That is a deployment fact, not something the person drawing can act on, and a red "Not
     * saved" badge they cannot fix is noise on every screen. It goes quiet and keeps the sentence
     * for anyone who looks — the work is on the device either way, which is where it has always
     * been.
     */
    if (/relation .*work.* does not exist|Could not find the table/i.test(said)) {
      state.value = 'off';
      lastTrouble.value = 'This build has no workspace table yet, so work stays on this device.';
      return null;
    }

    state.value = looksOffline ? 'offline' : 'trouble';
    lastTrouble.value = looksOffline ? null : said;
    return null;
  }
}

// ── The ticker ─────────────────────────────────────────────────────────────────────────────

let timer: ReturnType<typeof setTimeout> | null = null;
let running: Promise<unknown> | null = null;
let again = false;

/**
 * Something changed. Save it, once the pen has been still for a moment.
 *
 * Called from `lib/workspace.ts` after every local write, so no surface has to remember to. Two
 * guards: a debounce, so a stroke is not a round trip, and a single-flight, so a slow pass cannot
 * overlap the next one — an edit made during a pass sets `again` and is picked up when it lands.
 */
export function touch(): void {
  if (!syncable.value) { state.value = 'off'; return; }
  if (state.value !== 'saving') state.value = 'pending';
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => { void run(); }, QUIET_MS);
}

async function run(force = false): Promise<SyncReport | null> {
  if (running) { again = true; return null; }
  running = sync(force);
  try {
    return (await running) as SyncReport | null;
  } finally {
    running = null;
    if (again) { again = false; touch(); }
  }
}

/** The Save now button, and what a returning network presses. */
export const saveNow = (): Promise<SyncReport | null> => {
  if (timer) clearTimeout(timer);
  return run(true);
};

let listening = false;

/**
 * Start the ticker for this device.
 *
 * Idempotent. `online` is the event that matters: an evening of work done on a train has to reach
 * the account the moment there is a network, without anybody pressing anything.
 */
export function watchNetwork(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  // Every write in `lib/storage.ts` says so on this event, so no surface has to remember to.
  window.addEventListener('euclid:wrote', () => { touch(); });
  window.addEventListener('online', () => { void saveNow(); });
  window.addEventListener('offline', () => {
    if (syncable.value) state.value = 'offline';
  });
  // A tab being put away is the last chance to catch an edit inside the quiet window.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && state.value === 'pending') void run();
  });
}

/** Test seam: forget the watermarks and the state this module is holding. */
export function resetSync(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  running = null;
  again = false;
  state.value = 'off';
  lastSavedAt.value = null;
  lastTrouble.value = null;
}
